/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { BASE_PATH } from '@/data/constants/common';
import { breakpoints } from '@/styles/HDM/breakpoints';
import { fonts } from '@/styles/HDM/fonts';
import { typography } from '@/styles/HDM/type';
import { getAllInheritedThemes, themeManifest, ThemeManifestTheme } from '@/styles/manifest';
import { HDMContainer, HDMIconButton } from '@/styles/themeCustomization';
import { createTheme, responsiveFontSizes, SxProps, ThemeOptions } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import { createContext, useCallback, useContext, useMemo } from 'react';
const ThemeSettingsContext = createContext<{
	additives?: ThemeManifestTheme['additives'];
}>({});
export const ThemeSettingsProvider = ThemeSettingsContext.Provider;

export const useStyleTheme = (
	themeName: keyof (typeof themeManifest)['themes'] = themeManifest['defaultTheme'] || 'Base'
) => {
	const rc = useMemo(() => {
		const { components, additives } = getAllInheritedThemes(themeName);
		const tempHDMComponents = components
			.map((muiHDMComponent) => {
				const component = 'components' in muiHDMComponent && muiHDMComponent.components;
				const oKeys = component && Object.keys(component);
				return oKeys;
			})
			.filter((value) => value !== false);

		const HDMComponents = Object.fromEntries(
			tempHDMComponents.flat().map((HDMComponent) => [
				HDMComponent,
				{
					styleOverrides: {
						root: ({ ownerState }: { ownerState: { alpha?: boolean } }) => ({
							...(ownerState.alpha && { opacity: 0.6, backdropFilter: 'blur(10px)' }),
						}),
					},
				},
			])
		);

		const theme = responsiveFontSizes(
			createTheme(
				{
					components: {
						...HDMComponents,
						MuiIconButton: HDMIconButton,
						MuiContainer: HDMContainer,
					},
					...breakpoints,
					...fonts,
					...typography,
				},
				components.reduce<ThemeOptions>((composition, styles) => {
					const _styles: ThemeOptions = typeof styles === 'function' ? styles(BASE_PATH) : styles;
					return deepmerge(composition, _styles);
				}, {})
			)
		);
		return { theme, additives };
	}, [themeName]);
	return rc;
};

export const useThemeSettings = () => {
	const { additives } = useContext(ThemeSettingsContext);
	const { theme } = useStyleTheme();
	const getAdditive = useCallback(
		(additiveName: string): SxProps => {
			const additive = additives?.[additiveName] || {};
			const rc = typeof additive === 'function' ? additive(theme) : additive;
			return rc;
		},
		[additives, theme]
	);
	return { additives, getAdditive };
};
